import { makeAutoObservable, observable, runInAction } from 'mobx';
import { extensionCatalog, type ExtensionCatalog } from '../catalog';
import { installExtension } from '../install';
import { BrowserPackagePersistence, type PackagePersistence } from './persistence';
import { loadPackageCode, type LoadedPackage, type PackageLoader } from './loader';
import { PACKAGE_LIMITS, validateContent, type SavedPackage } from './manifest';

export interface PackageEntry {
  record: SavedPackage;
  loaded: boolean;
  pendingReload: boolean;
  removed: boolean;
  error: string | null;
}
const message = (error: unknown) =>
  (error instanceof Error ? error.message : String(error)).slice(0, 500);
export class ExtensionPackageStore {
  entries = new Map<string, PackageEntry>();
  unavailable = new Map<string, string>();
  ready = false;
  busy = false;
  error: string | null = null;
  externalChange = false;
  recoveryMode = false;
  private boot?: Promise<void>;
  private loaded = new Map<string, { digest: string; resource: LoadedPackage }>();
  private channel?: BroadcastChannel;
  constructor(
    private readonly persistence: PackagePersistence = new BrowserPackagePersistence(),
    private readonly loader: PackageLoader = loadPackageCode,
    private readonly catalog: ExtensionCatalog = extensionCatalog,
    private readonly install = installExtension,
  ) {
    makeAutoObservable<
      this,
      'persistence' | 'loader' | 'catalog' | 'install' | 'loaded' | 'boot' | 'channel'
    >(
      this,
      {
        entries: observable.shallow,
        unavailable: observable.shallow,
        persistence: false,
        loader: false,
        catalog: false,
        install: false,
        loaded: false,
        boot: false,
        channel: false,
      },
      { autoBind: true },
    );
  }
  initialize(recoveryMode = false) {
    if (this.boot) return this.boot;
    this.recoveryMode = recoveryMode;
    return (this.boot = this.loadInstalled());
  }
  private async loadInstalled() {
    try {
      const records = await this.persistence.list();
      if (records.length > PACKAGE_LIMITS.installed)
        runInAction(() => {
          this.recoveryMode = true;
          this.error =
            'Too many saved extension packages. Community code is off; remove packages to reach the limit of 16, then reload.';
        });
      const enabled: string[] = [];
      for (const record of records) {
        try {
          const content = validateContent(record.manifest, record.files);
          if (
            !(record.archive instanceof Uint8Array) ||
            record.archive.length > PACKAGE_LIMITS.archive ||
            typeof record.enabled !== 'boolean' ||
            !/^[a-f0-9]{64}$/.test(record.digest)
          )
            throw new Error('Invalid saved package record.');
          const validated = { ...record, ...content };
          runInAction(() =>
            this.entries.set(content.manifest.id, {
              record: validated,
              loaded: false,
              pendingReload: false,
              removed: false,
              error: null,
            }),
          );
          if (record.enabled && !this.recoveryMode) enabled.push(content.manifest.id);
        } catch (error) {
          runInAction(() => {
            const id = record?.manifest?.id;
            if (typeof id === 'string') this.unavailable.set(id, message(error));
            else this.error = `A saved extension could not be loaded: ${message(error)}`;
          });
        }
      }
      await Promise.all(enabled.map((id) => this.enableNow(id)));
    } catch (error) {
      runInAction(() => {
        this.error = `Extension storage is unavailable: ${message(error)}`;
      });
    } finally {
      runInAction(() => {
        this.ready = true;
      });
      if (typeof BroadcastChannel !== 'undefined') {
        this.channel = new BroadcastChannel('digitiles-extension-packages');
        this.channel.onmessage = () =>
          runInAction(() => {
            this.externalChange = true;
          });
      }
    }
  }
  private async enableNow(id: string) {
    const entry = this.entries.get(id)!;
    let resource: LoadedPackage | undefined;
    try {
      resource = await this.loader(entry.record);
      this.install(resource.manifest, this.catalog);
      this.catalog.setPaletteEnabled(id, true);
      this.loaded.set(id, { digest: entry.record.digest, resource });
      runInAction(() =>
        this.entries.set(id, {
          ...entry,
          loaded: true,
          pendingReload: false,
          error: null,
        }),
      );
    } catch (error) {
      resource?.dispose();
      runInAction(() =>
        this.entries.set(id, { ...entry, error: `Unable to enable: ${message(error)}` }),
      );
    }
  }
  private async change(operation: () => Promise<void>) {
    if (this.externalChange)
      throw new Error(
        'Packages changed in another tab. Reload before making more changes.',
      );
    if (this.busy) throw new Error('Wait for the current extension operation to finish.');
    this.busy = true;
    try {
      await operation();
      this.channel?.postMessage('changed');
    } finally {
      runInAction(() => {
        this.busy = false;
      });
    }
  }
  async installPackage(record: SavedPackage) {
    await this.change(async () => {
      const content = validateContent(record.manifest, record.files);
      const id = content.manifest.id;
      if (!this.entries.has(id) && this.entries.size >= PACKAGE_LIMITS.installed)
        throw new Error(
          'Remove a package before installing more (limit 16); reload to finish pending removals.',
        );
      const current = this.entries.get(id);
      if (current?.record.digest === record.digest && !current.removed)
        throw new Error('This exact package is already installed.');
      if (this.catalog.installed.has(id) && !this.loaded.has(id))
        throw new Error('This extension ID belongs to an installed bundled extension.');
      const next = { ...record, ...content, enabled: false };
      await this.persistence.put(next);
      const loaded = this.loaded.has(id);
      if (loaded) {
        this.catalog.setRuntimeEnabled(id, false);
        this.catalog.setPaletteEnabled(id, false);
      }
      runInAction(() =>
        this.entries.set(id, {
          record: next,
          loaded,
          pendingReload: loaded,
          removed: false,
          error: null,
        }),
      );
    });
  }
  async setEnabled(id: string, enabled: boolean) {
    await this.change(async () => {
      const entry = this.entries.get(id);
      if (!entry || entry.removed) throw new Error('Package is no longer installed.');
      const record = { ...entry.record, enabled };
      await this.persistence.put(record);
      const loaded = this.loaded.get(id);
      const sameCode = loaded?.digest === record.digest;
      const pendingReload = enabled
        ? this.recoveryMode || (!sameCode && (!!loaded || record.manifest.reloadRequired))
        : !!loaded;
      runInAction(() =>
        this.entries.set(id, { ...entry, record, pendingReload, error: null }),
      );
      if (!enabled) {
        this.catalog.setRuntimeEnabled(id, false);
        this.catalog.setPaletteEnabled(id, false);
      } else if (sameCode && !this.recoveryMode) {
        this.catalog.setPaletteEnabled(id, true);
        runInAction(() =>
          this.entries.set(id, { ...this.entries.get(id)!, pendingReload: false }),
        );
      } else if (!pendingReload) await this.enableNow(id);
    });
  }
  async removePackage(id: string) {
    await this.change(async () => {
      const entry = this.entries.get(id);
      if (!entry) return;
      await this.persistence.remove(id);
      this.catalog.setRuntimeEnabled(id, false);
      this.catalog.setPaletteEnabled(id, false);
      const loaded = this.loaded.has(id);
      runInAction(() => {
        if (loaded)
          this.entries.set(id, {
            ...entry,
            removed: true,
            pendingReload: true,
            record: { ...entry.record, enabled: false },
          });
        else this.entries.delete(id);
      });
    });
  }
  async removeUnavailable(id: string) {
    await this.change(async () => {
      await this.persistence.remove(id);
      runInAction(() => this.unavailable.delete(id));
    });
  }
  dispose() {
    this.channel?.close();
    this.channel = undefined;
    for (const { resource } of this.loaded.values()) resource.dispose();
  }
}
export const extensionPackages = new ExtensionPackageStore();

if (import.meta.hot) import.meta.hot.accept(() => window.location.reload());
